import { useState, useEffect, useCallback } from 'react';
import { CommentRecord } from 'shared/types/contentTypes';
import {
  createComment,
  updateComment,
  deleteComment,
  reactToComment,
  getComments
} from '../api/api';
import { useAuth } from './useAuth';
import logger from '../utils/logger';

export interface UseContentCommentsProps {
  contentId: string;
  contentType: string;
  contentTitle: string;
  hiddenParentCommentId?: string; // When provided, fetch replies to this comment instead of top-level comments
  limit?: number;
  fetchComments?: boolean; // Whether to fetch comments eagerly
  includeReplies?: boolean; // If true, include all comments including replies (useful for singleThreadMode)
}

/**
 * Hook for managing comments on a single content item
 * Supports "hidden parent comment" pattern for showing replies as top-level comments
 */
export const useContentComments = (props: UseContentCommentsProps) => {
  const {
    contentId,
    contentType,
    contentTitle,
    hiddenParentCommentId,
    limit = 20,
    fetchComments = true,
    includeReplies = false
  } = props;
  
  // State
  const [comments, setComments] = useState<CommentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  const { currentUser2: currentUser, userProfile } = useAuth();
  
  // Fetch comments for the content item
  const fetchContentComments = useCallback(async () => {
    if (!fetchComments || !contentId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let fetchedComments: CommentRecord[];

      if (hiddenParentCommentId) {
        // Fetch replies to the hidden parent comment
        const repliesResponse = await getComments({
          contentId,
          parentCommentId: hiddenParentCommentId,
          limit
        });
        fetchedComments = repliesResponse.comments;
      } else {
        // Fetch top-level comments (or all comments if includeReplies is true)
        const commentsResponse = await getComments({
          contentId,
          limit,
          includeReplies
        });
        fetchedComments = commentsResponse.comments;
      }

      // Add content context to comments
      const enrichedComments = fetchedComments.map(comment => ({
        ...comment,
        contentContext: {
          contentId,
          contentType,
          contentTitle
        }
      }));

      setComments(enrichedComments);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load comments'));
      logger.error("Error loading comments:", err);
    } finally {
      setIsLoading(false);
    }
  }, [contentId, hiddenParentCommentId, limit, fetchComments, contentType, contentTitle, includeReplies]);
  
  // Add a comment
  const addComment = useCallback(async (text: string): Promise<CommentRecord | null> => {
    if (!currentUser) return null;
    
    try {
      let newComment: CommentRecord;
      
      if (hiddenParentCommentId) {
        // In hidden parent mode, all new comments are replies to the hidden parent
        newComment = await createComment({
          contentId,
          text,
          parentCommentId: hiddenParentCommentId
        });
      } else {
        // Regular top-level comment
        newComment = await createComment({
          contentId,
          text
        });
      }
      
      // Add user info and content context to the comment
      const enrichedComment = {
        ...newComment,
        username: userProfile?.displayName || userProfile?.name || currentUser?.username || 'User',
        userProfilePicUrl: userProfile?.profilePictureUrl,
        contentContext: {
          contentId,
          contentType,
          contentTitle
        }
      };
      
      // Update the state with the new comment
      setComments(prev => [enrichedComment, ...prev]);
      
      return newComment;
    } catch (err) {
      console.error("Error in addComment:", err);
      setError(err instanceof Error ? err : new Error('Failed to add comment'));
      return null;
    }
  }, [contentId, hiddenParentCommentId, currentUser, userProfile, contentType, contentTitle]);
  
  // Add a reply to a comment
  const addReply = useCallback(async (text: string, parentCommentId: string): Promise<CommentRecord | null> => {
    if (!currentUser) return null;
    
    try {
      // Create the reply
      const newReply = await createComment({
        contentId,
        text,
        parentCommentId
      });
      
      // Add user info and content context to the reply
      const enrichedReply = {
        ...newReply,
        username: userProfile?.displayName || userProfile?.name || currentUser?.username || 'User',
        userProfilePicUrl: userProfile?.profilePictureUrl,
        contentContext: {
          contentId,
          contentType,
          contentTitle
        }
      };
      
      // Update the state with the new reply
      setComments(prev => [...prev, enrichedReply]);
      
      return newReply;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to add reply'));
      return null;
    }
  }, [contentId, currentUser, userProfile, contentType, contentTitle]);
  
  // Edit a comment
  const editComment = useCallback(async (commentId: string, text: string): Promise<CommentRecord | null> => {
    if (!currentUser) return null;
    
    try {
      // Update the comment via API
      const updatedComment = await updateComment({
        contentId,
        commentId,
        text
      });
      
      // Update the comment in the state
      setComments(prev => 
        prev.map(comment => 
          comment.commentId === commentId
            ? { ...comment, ...updatedComment }
            : comment
        )
      );
      
      return updatedComment;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to edit comment'));
      return null;
    }
  }, [contentId, currentUser]);
  
  // Delete a comment
  const removeComment = useCallback(async (commentId: string): Promise<boolean> => {
    if (!currentUser) return false;
    
    try {
      // Delete the comment via API
      await deleteComment({
        contentId,
        commentId
      });
      
      // Mark the comment as deleted in state
      setComments(prev => 
        prev.map(comment => 
          comment.commentId === commentId
            ? { ...comment, isDeleted: true, text: '[Comment deleted]' }
            : comment
        )
      );
      
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete comment'));
      return false;
    }
  }, [contentId, currentUser]);
  
  // React to a comment (resonate/un-resonate)
  const likeComment = useCallback(async (commentId: string, isResonated: boolean): Promise<boolean> => {
    if (!currentUser) return false;
    
    try {
      // Update reaction via API
      const reaction = await reactToComment({
        contentId,
        commentId,
        reactionType: 'resonate',
        action: isResonated ? 'add' : 'remove'
      });
      
      // Update the comment in the state
      setComments(prev => 
        prev.map(comment => 
          comment.commentId === commentId
            ? {
                ...comment,
                reactions: {
                  resonates: reaction.totalReactions,
                  userReaction: reaction.currentUserReaction ? 'resonate' : undefined
                }
              }
            : comment
        )
      );
      
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to resonate with comment'));
      return false;
    }
  }, [contentId, currentUser]);
  
  // Initialize data fetching
  useEffect(() => {
    fetchContentComments();
  }, [fetchContentComments]);
  
  return {
    comments,
    isLoading,
    error,
    addComment,
    addReply,
    editComment,
    removeComment,
    likeComment,
    refreshComments: fetchContentComments
  };
};

export default useContentComments;